import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Search, Loader2, CheckCircle, AlertCircle, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

const AddVehicleDialog = ({ open, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    nickname: '',
    year: new Date().getFullYear(),
    make: '',
    model: '',
    trim: '',
    engine_size: '',
    vin: '',
    current_mileage: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [vinLookupStatus, setVinLookupStatus] = useState(null); // 'success', 'error', null
  const [vinLookupMessage, setVinLookupMessage] = useState('');

  const handleVinLookup = async () => {
    const vin = formData.vin.trim().toUpperCase();
    
    if (vin.length !== 17) {
      toast.error('VIN must be exactly 17 characters');
      return;
    }
    
    setLookingUp(true);
    setVinLookupStatus(null);
    setVinLookupMessage('');
    
    try {
      const response = await API.get(`/vehicles/vin-lookup/${vin}`);
      
      if (response.data.success && response.data.vehicle) {
        const vehicle = response.data.vehicle;
        
        // Auto-fill the form with the decoded data
        setFormData(prev => ({
          ...prev,
          vin: vin,
          year: vehicle.year || prev.year,
          make: vehicle.make || prev.make,
          model: vehicle.model || prev.model,
          trim: vehicle.trim || prev.trim,
          engine_size: vehicle.engine_size || prev.engine_size,
          // Auto-generate nickname if empty
          nickname: prev.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`.trim(),
        }));
        
        setVinLookupStatus('success');
        setVinLookupMessage(`Found: ${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? ' ' + vehicle.trim : ''}`);
        toast.success('Vehicle info loaded from VIN!');
      } else {
        setVinLookupStatus('error');
        setVinLookupMessage(response.data.error || 'Could not decode VIN');
        toast.error(response.data.error || 'VIN lookup failed');
      }
    } catch (error) {
      console.error('VIN lookup error:', error);
      setVinLookupStatus('error');
      setVinLookupMessage(error.response?.data?.detail || 'VIN lookup failed');
      toast.error(error.response?.data?.detail || 'VIN lookup failed');
    } finally {
      setLookingUp(false);
    }
  };

  const handleVinChange = (e) => {
    const vin = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
    setFormData({ ...formData, vin });
    
    // Reset lookup status when VIN changes
    if (vinLookupStatus) {
      setVinLookupStatus(null);
      setVinLookupMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        nickname: formData.nickname,
        year: parseInt(formData.year),
        make: formData.make,
        model: formData.model,
        trim: formData.trim || undefined,
        engine_size: formData.engine_size || undefined,
        vin: formData.vin || undefined,
        current_mileage: formData.current_mileage ? parseInt(formData.current_mileage) : undefined,
        notes: formData.notes || undefined,
      };

      await onAdd(payload);

      // Reset form
      setFormData({
        nickname: '',
        year: new Date().getFullYear(),
        make: '',
        model: '',
        trim: '',
        engine_size: '',
        vin: '',
        current_mileage: '',
        notes: '',
      });
      setVinLookupStatus(null);
      setVinLookupMessage('');
    } finally {
      setLoading(false);
    }
  };

  const canLookupVin = formData.vin.length === 17 && !lookingUp;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Vehicle</DialogTitle>
          <DialogDescription>
            Add your vehicle details to start tracking maintenance, warranties, and receipts.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* VIN Lookup Section - Prominent */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Wand2 className="h-5 w-5 text-blue-600" />
                <Label htmlFor="vin" className="text-blue-800 font-medium">Quick Start: Enter VIN</Label>
              </div>
              <div className="flex gap-2">
                <Input
                  id="vin"
                  placeholder="Enter 17-character VIN"
                  maxLength={17}
                  value={formData.vin}
                  onChange={handleVinChange}
                  className="font-mono uppercase"
                  data-testid="vehicle-vin-input"
                />
                <Button
                  type="button"
                  onClick={handleVinLookup}
                  disabled={!canLookupVin}
                  className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                >
                  {lookingUp ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Looking up...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Lookup VIN
                    </>
                  )}
                </Button>
              </div>
              
              {/* VIN Lookup Status */}
              {vinLookupStatus && (
                <div className={`mt-2 flex items-center gap-2 text-sm ${
                  vinLookupStatus === 'success' ? 'text-green-700' : 'text-red-600'
                }`}>
                  {vinLookupStatus === 'success' ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  {vinLookupMessage}
                </div>
              )}
              
              <p className="text-xs text-blue-600 mt-2">
                {formData.vin.length}/17 characters • VIN auto-fills year, make, model, trim, and engine
              </p>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Vehicle Details</span>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="nickname">Nickname *</Label>
              <Input
                id="nickname"
                placeholder="e.g., Blue Subaru, Family Car"
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                required
                data-testid="vehicle-nickname-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="year">Year *</Label>
                <Input
                  id="year"
                  type="number"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  required
                  data-testid="vehicle-year-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="make">Make *</Label>
                <Input
                  id="make"
                  placeholder="e.g., Toyota"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  required
                  data-testid="vehicle-make-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  placeholder="e.g., Camry"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  required
                  data-testid="vehicle-model-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="trim">Trim</Label>
                <Input
                  id="trim"
                  placeholder="e.g., XLE"
                  value={formData.trim}
                  onChange={(e) => setFormData({ ...formData, trim: e.target.value })}
                  data-testid="vehicle-trim-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="engine_size">Engine Size</Label>
                <Input
                  id="engine_size"
                  placeholder="e.g., 2.5L 4-cyl"
                  value={formData.engine_size}
                  onChange={(e) => setFormData({ ...formData, engine_size: e.target.value })}
                  data-testid="vehicle-engine-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="current_mileage">Current Mileage</Label>
                <Input
                  id="current_mileage"
                  type="number"
                  placeholder="e.g., 50000"
                  value={formData.current_mileage}
                  onChange={(e) => setFormData({ ...formData, current_mileage: e.target.value })}
                  data-testid="vehicle-mileage-input"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Any additional notes about this vehicle..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="vehicle-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-vehicle-button"
            >
              {loading ? 'Adding...' : 'Add Vehicle'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddVehicleDialog;
